import { useRouter } from 'expo-router';
import { Home, Building2, Building, Caravan } from 'lucide-react-native';
import { useEffect, useState } from 'react';
import { StyleSheet, Text, TextInput, View } from 'react-native';

import { OptionRow, PrimaryCTA } from '@/components/onboarding/Controls';
import { Eyebrow, H1, Screen } from '@/components/onboarding/Screen';
import { YearPicker } from '@/components/YearPicker';
import { Fonts, Palette, Spacing } from '@/constants/theme';
import type { HomeType } from '@/db/types';
import { useOnboarding } from '@/store/onboarding';
import { webNoOutline } from '@/lib/web-style';

const OPTIONS: { v: HomeType; title: string; sub: string; Icon: typeof Home }[] = [
  { v: 'house', title: 'Single-family house', sub: 'Detached, with its own land', Icon: Home },
  { v: 'town', title: 'Townhouse / row home', sub: 'Shared walls, some yard', Icon: Building2 },
  { v: 'condo', title: 'Condo or apartment', sub: 'Interior-only responsibility', Icon: Building },
  { v: 'mobile', title: 'Mobile / manufactured', sub: 'Specialized systems', Icon: Caravan },
];

export default function HomeTypeScreen() {
  const router = useRouter();
  const setHomeType = useOnboarding((s) => s.setHomeType);
  const setHomeName = useOnboarding((s) => s.setHomeName);
  const setYearBuilt = useOnboarding((s) => s.setYearBuilt);
  const setOwnedSince = useOnboarding((s) => s.setOwnedSince);
  const stored = useOnboarding((s) => s.homeType);
  const storedName = useOnboarding((s) => s.homeName);
  const storedYearBuilt = useOnboarding((s) => s.yearBuilt);
  const storedOwnedSince = useOnboarding((s) => s.ownedSince);
  // Default to single-family. ~80% of US owner-occupied homes are
  // single-family detached, so pre-selecting the modal answer keeps
  // the most-common path frictionless. The other 20% see all four
  // tiles and can still pick.
  const [picked, setPicked] = useState<HomeType | null>(stored ?? 'house');
  useEffect(() => {
    if (!stored) setHomeType('house');
  }, [stored, setHomeType]);
  // Local state for the input so the user can type freely without
  // every keystroke causing a store re-render. We commit on Continue
  // (or before navigating away).
  const [name, setName] = useState(storedName ?? '');
  const lastName = useOnboarding((s) => s.ownerLastName);
  // Years use the YearPicker (vertical scroll list) instead of a free
  // TextInput. We own the input and the user can't type garbage years
  // like "20243" or "ab".
  const [yearBuilt, setYearBuiltLocal] = useState<number | null>(storedYearBuilt);
  const [ownedSinceLocal, setOwnedSinceLocal] = useState<number | null>(storedOwnedSince);

  const pick = (v: HomeType) => {
    setPicked(v);
    setHomeType(v);
    // Don't auto-advance any more — we want the user to see the name
    // input and decide whether to fill it. Continue button drives nav.
  };

  const next = () => {
    setHomeName(name.trim());
    setYearBuilt(yearBuilt);
    // Owned-since sanity check — can't own a home before it was built.
    // YearPicker already constrains both fields to real years, so the
    // only invalid case here is owned < built; in that case we keep
    // owned-since blank and the user can re-pick.
    const goodOwned =
      ownedSinceLocal != null &&
      (yearBuilt == null || ownedSinceLocal >= yearBuilt)
        ? ownedSinceLocal
        : null;
    setOwnedSince(goodOwned);
    router.push('/(onboarding)/year' as never);
  };

  return (
    <Screen
      scroll
      footer={<PrimaryCTA label="Continue" disabled={!picked} onPress={next} />}>
      <Eyebrow>2 · Your home</Eyebrow>
      <H1>What kind of{'\n'}home is it?</H1>
      <View style={{ marginTop: Spacing.five, gap: Spacing.two }}>
        {OPTIONS.map(({ v, title, sub, Icon }) => (
          <OptionRow
            key={v}
            icon={
              <Icon
                size={22}
                color={picked === v ? Palette.cream : Palette.forest}
                strokeWidth={1.7}
              />
            }
            title={title}
            subtitle={sub}
            selected={picked === v}
            onPress={() => pick(v)}
          />
        ))}
      </View>

      {/*
        Wave 65 (user call): home name + build year were a cramped
        "optional" footnote row — but the name is the emotional hook
        (this manual is for SOMEWHERE) and the build year is the single
        highest-leverage planning fact. Each now gets its own card:
        name with a big serif-framed input + suggestion chips, year
        with a plain-spoken "why this matters" and the owned-since
        picker riding along. Still skippable — promoted, not gated.
      */}
      <View style={styles.heroCard}>
        <Text style={styles.heroKicker}>NAME YOUR HOMESTEAD</Text>
        <Text style={styles.heroWhy}>
          This manual is for <Text style={styles.heroWhyEm}>somewhere</Text> —
          give it the name you actually use. It greets you on the home
          screen and headlines every export.
        </Text>
        <TextInput
          value={name}
          onChangeText={setName}
          placeholder={
            picked === 'house'
              ? lastName.trim()
                ? `The ${lastName.trim()} Home, The Bungalow…`
                : 'The Bungalow, Gran’s House, 42 Maple…'
              : picked === 'condo'
              ? 'Apartment 4B, The Loft…'
              : picked === 'town'
              ? 'The Row House, Our Place…'
              : 'The Homestead…'
          }
          placeholderTextColor={Palette.inkFaint}
          autoCapitalize="words"
          autoCorrect={false}
          // Capped at 30 so the home name + 4-icon menu on the My
          // Home title row fits without truncation on most phones.
          maxLength={30}
          onBlur={() => setHomeName(name.trim())}
          style={[styles.heroInput, webNoOutline]}
        />
      </View>

      <View style={styles.heroCard}>
        <Text style={styles.heroKicker}>WHEN WAS IT BUILT?</Text>
        <Text style={styles.heroWhy}>
          The most useful single fact about your home — it tunes
          aging-system checks, sealing cadence, and pre-fills install
          years on everything you add. Not sure? Pick an era on the
          next screen instead.
        </Text>
        <View style={styles.yearRow}>
          <View style={{ flex: 1, minWidth: 130 }}>
            <Text style={styles.label}>BUILD YEAR</Text>
            <YearPicker
              value={yearBuilt}
              onChange={(y) => {
                setYearBuiltLocal(y);
                setYearBuilt(y); // live-commit so Back never loses it (Wave 74)
              }}
              placeholder="Pick a year"
              minYear={1700}
              title="Build year"
            />
          </View>
          <View style={{ flex: 1, minWidth: 130 }}>
            <Text style={styles.label}>OWNED SINCE</Text>
            <YearPicker
              value={ownedSinceLocal}
              onChange={(y) => {
                setOwnedSinceLocal(y);
                setOwnedSince(y);
              }}
              placeholder="Pick a year"
              minYear={yearBuilt ?? 1900}
              title="Owned since"
            />
          </View>
        </View>
      </View>

    </Screen>
  );
}

const styles = StyleSheet.create({
  heroCard: {
    marginTop: Spacing.four,
    backgroundColor: Palette.card,
    borderWidth: 1.5,
    borderColor: Palette.line,
    borderRadius: 18,
    padding: Spacing.four,
  },
  heroKicker: {
    fontSize: 12.5,
    fontFamily: Fonts.sansBold,
    color: Palette.honeyDeep,
    letterSpacing: 1,
  },
  heroWhy: {
    marginTop: 6,
    fontSize: 13,
    lineHeight: 19,
    fontFamily: Fonts.sansMedium,
    color: Palette.inkSoft,
  },
  heroWhyEm: {
    fontFamily: Fonts.sansBold,
    color: Palette.ink,
    fontStyle: 'italic',
  },
  heroInput: {
    marginTop: 12,
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.sand,
    borderRadius: 14,
    paddingHorizontal: 16,
    paddingVertical: 14,
    fontFamily: Fonts.serifBold,
    fontSize: 19,
    color: Palette.ink,
  },
  yearRow: {
    marginTop: 12,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.two,
    alignItems: 'flex-start',
  },
  label: {
    fontSize: 12,
    fontFamily: Fonts.sansBold,
    color: Palette.inkFaint,
    letterSpacing: 0.8,
    marginBottom: 6,
  },
});
